import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';

/*
  Usa diretamente o `useQueryClient` do tanstack porque o wrapper
  interno (`hooks/useQueryClient`) só expõe um subset e não inclui
  `getQueriesData` — necessário aqui pra varrer as queries de lista
  de comentários em cache independente do sort atual.
*/
import {
  useQuery,
  useQueryClient as useTanstackQueryClient,
} from '@tanstack/react-query';
import { $insertNodes, COMMAND_PRIORITY_EDITOR, createCommand } from 'lexical';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { $createMentionNode } from 'components/TextEditor/Nodes/MentionNode';

import { merge } from 'lib/array';
import { commentQueryKeys, userQueryKeys } from 'lib/queryKeys';
import { $createWhitespace, getAtSignMentionMatch } from 'lib/textEditor';
import { filterUsersByWord } from 'lib/user';

import { useDomain } from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import {
  getArticleCommentMentionsList,
  getOfferCommentMentionsList,
  getThreadCommentMentionsList,
} from 'services/comment';
import { getUserFollowingList } from 'services/user';

import MentionTypeaheadOption from './MentionTypeaheadOption';

const MentionAutocomplete = dynamic(() => import('./MentionAutocomplete'), {
  ssr: false,
});

const ENDPOINTS = {
  article: (domainId, opts) =>
    getArticleCommentMentionsList(
      {
        articleId: domainId,
      },
      opts
    ),
  offer: (domainId, opts) =>
    getOfferCommentMentionsList(
      {
        offerId: domainId,
      },
      opts
    ),
  thread: (domainId, opts) =>
    getThreadCommentMentionsList(
      {
        threadId: domainId,
      },
      opts
    ),
};
export const INSERT_MENTION_COMMAND = createCommand('mention');

const getNodesSeparatedByWhitespace = (nodes) => {
  const nodesWithWhitespace = [];

  for (const node of nodes) {
    nodesWithWhitespace.push(node, $createWhitespace());
  }

  return nodesWithWhitespace;
};

/*
  Fallback pros autocompletes locais — em dev/CI sem endpoint de
  menções, extraímos os autores dos comentários já carregados no cache
  do tanstack pra popular a lista. Em prod, usersFromMentions traz o
  set completo e esse fallback vira subset; então deixar ativo sempre
  não muda o comportamento em produção.
*/
const extractCommentAuthors = (pages = []) => {
  const authors = [];
  const seen = new Set();

  const pushUser = (user) => {
    if (
      !user ||
      typeof user.userId !== 'number' ||
      !user.userUsername ||
      seen.has(user.userId)
    ) {
      return;
    }

    seen.add(user.userId);
    authors.push({
      userId: user.userId,
      userName: user.userName || user.userUsername,
      userPhoto: user.userPhoto || '',
      userUsername: user.userUsername,
    });
  };

  pages.flat().forEach((comment) => {
    if (!comment) return;
    pushUser(comment);
    (comment.replies || []).forEach(pushUser);
  });

  return authors;
};

const MentionsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [wordInSelection, setWordInSelection] = useState(null);
  const queryClient = useTanstackQueryClient();
  const { domain, domainId } = useDomain();
  const { isLoggedIn, LoggedInUser } = useUser();

  /*
    `wordInSelection` pode ser "" quando o usuário acabou de digitar
    `@` e ainda não escreveu nenhuma letra. Usar `!!wordInSelection`
    travava o autocomplete nesse instante (string vazia é falsy), então
    a listagem só aparecia depois do segundo caractere. Com
    `wordInSelection !== null` disparamos a query assim que o `@` é
    digitado e o usuário vê a lista imediatamente — mesmo
    comportamento do Pelando.
  */
  const isMentionQueryEnabled =
    isLoggedIn && !!domainId && wordInSelection !== null;

  const { data: usersFromMentions, isSuccess: isUsersFromMentionsSuccess } =
    useQuery({
      queryKey: commentQueryKeys[domain]?.mentions({ domainId }),
      queryFn: ({ signal }) =>
        (ENDPOINTS[domain] || ENDPOINTS.offer)(domainId, { signal }),
      enabled: isMentionQueryEnabled,
    });

  const { data: followedUsers, isSuccess: isFollowedUsersSuccess } = useQuery({
    queryKey: userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
    enabled: isMentionQueryEnabled,
    queryFn: ({ signal }) => getUserFollowingList({ signal }),
  });

  useEffect(() => {
    const cleanup = editor.registerCommand(
      INSERT_MENTION_COMMAND,
      (payload) => {
        editor.update(() => {
          const nodes = [];

          if (Array.isArray(payload)) {
            payload.forEach((userUsername) => {
              nodes.push($createMentionNode(userUsername));
            });
          } else {
            nodes.push($createMentionNode(payload));
          }

          $insertNodes(getNodesSeparatedByWhitespace(nodes));
        });

        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );

    return () => {
      cleanup();
    };
  }, [editor]);

  /*
    Autores dos comentários já em cache — usados como fallback quando a
    API de menções e/ou de seguidos não responde (local dev, CORS,
    backend sem endpoint). Pega a query `list` de comentários do domínio
    atual e agrega userIds únicos.
  */
  const commentAuthors = useMemo(() => {
    if (wordInSelection === null || !domainId) {
      return [];
    }

    const listQueries = queryClient.getQueriesData({
      queryKey: [commentQueryKeys.key, domain],
    });

    const pages = listQueries.flatMap(([, data]) => data?.pages || []);

    return extractCommentAuthors(pages);
  }, [queryClient, domain, domainId, wordInSelection]);

  const mentionableUsers = useMemo(() => {
    const hasRemoteData = isUsersFromMentionsSuccess || isFollowedUsersSuccess;

    if (!hasRemoteData && commentAuthors.length === 0) {
      return [];
    }

    const remoteUsers = merge(usersFromMentions, followedUsers, 'userId');
    const allUsers = merge(remoteUsers, commentAuthors, 'userId');

    return filterUsersByWord(allUsers, wordInSelection).map(
      (user) => new MentionTypeaheadOption(user)
    );
  }, [
    isUsersFromMentionsSuccess,
    isFollowedUsersSuccess,
    usersFromMentions,
    followedUsers,
    commentAuthors,
    wordInSelection,
  ]);

  const onSelectOption = useCallback(
    (selectedOption, nodeToReplace) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.userUsername);

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
          mentionNode.select();
        }

        return true;
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      menuRenderFn={(_, { selectedIndex, selectOptionAndCleanUp }) => (
        <MentionAutocomplete
          mentionableUsers={mentionableUsers}
          selectedIndex={selectedIndex}
          onAddMention={(option) => selectOptionAndCleanUp(option)}
        />
      )}
      options={mentionableUsers}
      triggerFn={getAtSignMentionMatch}
      onQueryChange={setWordInSelection}
      onSelectOption={onSelectOption}
    />
  );
};

export default MentionsPlugin;
